<template>
  <section v-if="activity" class="recording-activity">
    <h3 class="display-5 mb-3 activity-heading">
      <strong>{{ $t('recordings.activity.title') }}</strong>
      <a
        v-if="infoUrl"
        :href="infoUrl"
        target="_blank"
        rel="noopener"
        class="activity-info"
        :aria-label="$t('recordings.activity.learn_more', { topic: selectedTitle })"
        :title="$t('recordings.activity.learn_more', { topic: selectedTitle })"
      >
        <span class="material-icons" aria-hidden="true">info</span>
      </a>
    </h3>

    <div class="activity-tabs" role="tablist" :style="{ '--tab-columns': Math.max(cards.length, 4) }">
      <button
        v-for="card in cards"
        :key="card.tab"
        type="button"
        role="tab"
        class="activity-tab"
        :class="{ 'activity-tab--active': card.tab === selected }"
        :style="{ '--tab-color': TAB_STYLES[card.tab].color }"
        :aria-selected="card.tab === selected"
        @click="selected = card.tab"
      >
        <span class="activity-tab__title">
          <span class="material-icons activity-tab__icon">{{ TAB_STYLES[card.tab].icon }}</span>
          {{ card.title }}
        </span>
        <span class="activity-tab__value"><strong>{{ card.value }}</strong> <small>{{ card.unit }}</small></span>
      </button>
    </div>

    <p v-if="series?.profile" class="activity-legend" :class="{ 'activity-legend--hidden': !series.backdrop }">
      <span class="activity-legend__swatch"></span>{{ $t('recordings.activity.elevation_context') }}
    </p>

    <div class="activity-figures">
      <div v-for="figure in figures" :key="figure.label">
        <small class="text-muted d-block mb-1">{{ figure.label }}</small>
        <span class="activity-figures__value">
          <template v-if="figure.leading"><strong>{{ figure.leading.value }}</strong> <small>{{ figure.leading.unit }}</small>{{ ' ' }}</template>
          <strong>{{ figure.value }}</strong> <small>{{ figure.unit }}</small>
        </span>
      </div>
    </div>

    <p v-if="selected === 'pace' && activity.pace" class="activity-note text-muted">{{ stoppedNote(pref, t, locale) }}</p>

    <template v-if="series">
      <p class="activity-hint text-muted">
        {{ canHover ? $t('recordings.activity.hover_hint') : $t('recordings.activity.tap_hint') }}
      </p>
      <ActivityChart
        :series="series"
        :x-unit="pref.kilometerSymbol()"
        :backdrop-minimum-spread="toSmallUnit(MINIMUM_ELEVATION_SPREAD_METERS, pref)"
        :tooltip="tooltip"
        :high-point="highPoint"
      />
    </template>

    <template v-if="selected === 'ascent'">
      <div v-if="legend.length" class="ascent-legend">
        <span v-for="row in legend" :key="row.label" class="ascent-legend__row">
          <span class="ascent-legend__ramp" :style="{ background: `linear-gradient(90deg, ${row.colors.join(', ')})` }"></span>
          <strong>{{ row.label }}</strong> <span class="text-muted">{{ row.range }}</span>
        </span>
      </div>
      <template v-if="insight">
        <hr class="my-4">
        <p class="ascent-insight">
          <span class="material-icons ascent-insight__icon">{{ TAB_STYLES.ascent.icon }}</span>
          <i18n-t v-if="insight.fastest" keypath="recordings.activity.fastest_climbing" tag="span" scope="global">
            <template #rate><strong>{{ insight.rate }}</strong></template>
            <template #range>{{ insight.range }}</template>
            <template #gradient>{{ insight.gradient }}</template>
          </i18n-t>
          <i18n-t v-else keypath="recordings.activity.steepest_section" tag="span" scope="global">
            <template #range><strong>{{ insight.range }}</strong></template>
            <template #gradient>{{ insight.gradient }}</template>
          </i18n-t>
        </p>
      </template>
    </template>

    <PaceSplits v-if="selected === 'pace' && splitActivity" :activity="splitActivity" :pref="pref" />

    <template v-if="selected === 'heartRate' && activity.heartRate?.zones">
      <hr class="my-4">
      <HeartRateZones :zones="activity.heartRate.zones" />
    </template>

    <hr class="my-4">
  </section>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { MeasurementPrefUtility } from '../../utils/helpers/MeasurementPref';
import { MINIMUM_ELEVATION_SPREAD_METERS } from '../../utils/recordingActivity/colorScale';
import { toSmallUnit } from '../../utils/recordingActivity/format';
import {
  activityCard,
  activityFigures,
  activitySeries,
  activityTabs,
  activityTooltip,
  ascentInsight,
  ascentLegend,
  backdropHighPoint,
  defaultActivityTab,
  recordingActivity,
  stoppedNote,
  type ActivityTab,
  type ChartPoint,
  type RecordingActivityInput,
} from '../../utils/recordingActivity/recordingActivity';
import { hasSplits } from '../../utils/recordingActivity/paceSplits';
import { activityInfoUrl } from '../../utils/recordingActivity/supportArticles';
import { ACTIVITY_TAB_STYLES as TAB_STYLES } from '../../utils/recordingActivity/tabStyles';
import ActivityChart from './components/ActivityChart.vue';
import HeartRateZones from './components/HeartRateZones.vue';
import PaceSplits from './components/PaceSplits.vue';

const props = defineProps<{ input: RecordingActivityInput }>();

const { t, locale } = useI18n();
const pref = MeasurementPrefUtility.preference();
const canHover = typeof window.matchMedia !== 'function' || window.matchMedia('(hover: hover)').matches;

const activity = recordingActivity(props.input);
const tabs = activity ? activityTabs(activity) : [];
const selected = ref<ActivityTab>(defaultActivityTab(tabs));

const cards = computed(() => tabs.map((tab) => activityCard(tab, activity!, pref, t, locale.value)));
const figures = computed(() => activityFigures(selected.value, activity!, pref, t, locale.value));
const series = computed(() => activitySeries(selected.value, activity!, pref, t));
const legend = computed(() => (series.value ? ascentLegend(series.value, pref, t, locale.value) : []));
const highPoint = computed(() => backdropHighPoint(series.value, pref, t, locale.value));
const insight = computed(() => ascentInsight(activity!, pref, locale.value));
const infoUrl = computed(() => activityInfoUrl(selected.value));
const splitActivity = activity?.splitTrack && activity.pace && hasSplits(activity.totalMeters, pref.metresFromBigUnit(1))
  ? { ...activity, splitTrack: activity.splitTrack }
  : null;
const selectedTitle = computed(() => cards.value.find((card) => card.tab === selected.value)?.title ?? '');

function tooltip(point: ChartPoint) {
  return activityTooltip(point, series.value!, activity!, pref, t);
}
</script>

<style scoped>
.activity-heading {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.activity-info {
  display: inline-flex;
  color: var(--text-muted, #b2b2b2);
  text-decoration: none;
}

.activity-info:hover,
.activity-info:focus-visible {
  color: inherit;
}

.activity-info .material-icons {
  font-size: 1.25rem;
}

.activity-tabs {
  display: grid;
  grid-template-columns: repeat(var(--tab-columns), minmax(0, 1fr));
  gap: 0.75rem;
}

@media (max-width: 575.98px) {
  .activity-tabs {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.activity-tab {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  padding: 0.85rem 1rem;
  text-align: left;
  color: #fff;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 6px;
  transition: background-color 0.15s;
  /* MapButtons leaks a global `button { white-space: nowrap }` onto the page. */
  white-space: normal;
}

.activity-tab:hover {
  background: var(--gray-hover, #30323b);
}

.activity-tab--active,
.activity-tab--active:hover {
  background: var(--gray-raised, #2a2c34);
  border-color: transparent;
  box-shadow: inset 0 3px 0 var(--tab-color);
}

.activity-tab__title {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.85rem;
  font-weight: 700;
}

.activity-tab__icon {
  font-size: 16px;
  color: var(--tab-color);
}

.activity-tab__value strong {
  display: block;
  font-size: 1.3rem;
  line-height: 1.2;
}

.activity-tab__value small {
  display: block;
}

.activity-tab__value small,
.activity-figures__value small {
  color: var(--text-muted, #b2b2b2);
}

.activity-legend {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 1.25rem 0 0;
  font-size: 0.8rem;
  color: var(--text-muted, #b2b2b2);
}

.activity-legend--hidden {
  visibility: hidden;
}

.activity-legend__swatch {
  width: 14px;
  height: 9px;
  border-radius: 2px;
  background: rgba(255, 255, 255, 0.15);
}

.activity-figures {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem 3rem;
  margin-top: 1.25rem;
}

.activity-figures__value strong {
  font-size: 1.5rem;
}

.ascent-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem 2rem;
  margin-top: 1rem;
  font-size: 0.8rem;
}

.ascent-legend__row {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.ascent-legend__ramp {
  width: 48px;
  height: 8px;
  border-radius: 4px;
}

.ascent-insight {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin: 0;
}

.ascent-insight__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  font-size: 20px;
  color: #e0a85c;
  background: var(--gray-raised, #2a2c34);
}

.activity-note {
  margin: 0.75rem 0 0;
  font-size: 0.8rem;
}

.activity-hint {
  margin: 1.25rem 0 0.5rem;
  text-align: right;
  font-size: 0.75rem;
}
</style>
